import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, onAuthStateChanged, signOut } from 'firebase/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

const S = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#080b11', color: '#e7ecf3', fontFamily: 'Inter, system-ui, sans-serif' },
  card: { width: 'min(400px, 92vw)', background: '#0e131d', border: '1px solid #1e2735', borderRadius: 20, padding: 34, textAlign: 'center', boxShadow: '0 24px 80px rgba(0,0,0,.5)' },
  h: { fontSize: 22, fontWeight: 800, margin: '14px 0 6px' },
  sub: { fontSize: 13, color: '#8b94a3', marginBottom: 24, lineHeight: 1.5 },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 10, padding: '12px 22px', borderRadius: 12, border: '1px solid #2a3346', background: '#151c29', color: '#e7ecf3', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  out: { marginTop: 16, background: 'transparent', border: 'none', color: '#7f8a9a', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' },
};

let interceptorInstalled = false;

const isMobile = () => /Mobi|Android|iPhone|iPad|iPod/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
function friendlyErr(e) {
  const code = (e && e.code) || '';
  if (code === 'auth/unauthorized-domain') return 'This domain is not authorized in Firebase (Authentication → Settings → Authorized domains, project paperviz-7125d).';
  if (code === 'auth/popup-blocked') return 'Popup blocked — retrying with full-page redirect…';
  if (code === 'auth/popup-closed-by-user') return 'Sign-in closed before completing — please try again.';
  if (code === 'auth/network-request-failed') return 'Network error — check your connection and retry.';
  return code || String(e);
}

function AccountChip({ auth, email }) {
  const [open, setOpen] = useState(false);
  const initial = (email || '?').trim()[0].toUpperCase();
  return (
    <div style={{ position: 'fixed', top: 9, right: 12, zIndex: 300, fontFamily: 'Inter, system-ui, sans-serif' }}>
      {open && <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: -1 }} />}
      <button onClick={() => setOpen((o) => !o)} title={email} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px 5px 6px', borderRadius: 999,
        border: '1px solid #23304a', background: 'rgba(14,19,29,.9)', backdropFilter: 'blur(6px)',
        color: '#c9d2de', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
      }}>
        <span style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', fontSize: 11, fontWeight: 800 }}>{initial}</span>
        <span className="acct-email" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>
        <style>{'@media (max-width: 860px){ .acct-email{display:none} }'}</style>
        <span style={{ color: '#7f8a9a', fontSize: 9 }}>{open ? '\u25b2' : '\u25bc'}</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 40, right: 0, minWidth: 210, background: '#0e131d', border: '1px solid #23304a', borderRadius: 12, boxShadow: '0 16px 44px rgba(0,0,0,.5)', padding: 12 }}>
          <div style={{ fontSize: 10.5, color: '#8b94a3', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Signed in as</div>
          <div style={{ fontSize: 12.5, color: '#e7ecf3', margin: '4px 0 12px', wordBreak: 'break-all' }}>{email}</div>
          <button onClick={() => signOut(auth)} style={{
            width: '100%', padding: '9px 12px', borderRadius: 9, border: '1px solid #3a2530',
            background: 'rgba(248,113,113,.1)', color: '#f87171', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          }}>Sign out</button>
        </div>
      )}
    </div>
  );
}

export default function AuthGate({ children }) {
  const [st, setSt] = useState({ phase: 'loading' });

  useEffect(() => {
    (async () => {
      let cfg;
      try {
        cfg = (await axios.get(`${API_URL}/api/config`)).data;
      } catch (e) {
        setSt({ phase: 'ready' }); // config unreachable — don't brick the app
        return;
      }
      if (!cfg.authRequired) { setSt({ phase: 'ready' }); return; }
      const app = initializeApp(cfg.firebase);
      const auth = getAuth(app);
      if (!interceptorInstalled) {
        interceptorInstalled = true;
        axios.interceptors.request.use(async (req) => {
          const u = auth.currentUser;
          if (u) {
            try { req.headers.Authorization = `Bearer ${await u.getIdToken()}`; } catch (e) {}
          }
          return req;
        });
      }
      getRedirectResult(auth).catch((e) => { setSt((prev) => ({ ...prev, error: friendlyErr(e) })); });
      onAuthStateChanged(auth, async (user) => {
        if (!user) { setSt({ phase: 'login', auth }); return; }
        try {
          await axios.get(`${API_URL}/api/me`);
          setSt({ phase: 'ready', auth, email: user.email });
        } catch (e) {
          const denied = e.response && e.response.status === 403;
          setSt({ phase: denied ? 'denied' : 'login', auth, email: user.email });
        }
      });
    })();
  }, []);

  if (st.phase === 'ready') return (<>{children}{st.email && st.auth && <AccountChip auth={st.auth} email={st.email} />}</>);
  if (st.phase === 'loading') {
    return <div style={S.page}><div style={{ color: '#8b94a3', fontSize: 14 }}>Loading…</div></div>;
  }
  const doSignIn = async () => {
    const provider = new GoogleAuthProvider();
    setSt((p) => ({ ...p, error: null }));
    try {
      await signInWithPopup(st.auth, provider);
    } catch (e) {
      const code = (e && e.code) || '';
      // Popup failed/blocked/closed (common on phones) -> full-page redirect fallback.
      if (code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request'
          || code === 'auth/operation-not-supported-in-this-environment' || code === 'auth/popup-closed-by-user') {
        try { await signInWithRedirect(st.auth, provider); return; } catch (e2) { setSt((p) => ({ ...p, error: friendlyErr(e2) })); return; }
      }
      setSt((p) => ({ ...p, error: friendlyErr(e) }));
    }
  };
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={{ fontSize: 40 }}>🦔</div>
        <div style={S.h}>Hedgehog Trading</div>
        {st.phase === 'denied' ? (
          <>
            <div style={S.sub}>
              Signed in as <b style={{ color: '#e7ecf3' }}>{st.email}</b> — this account isn't on the invite list yet.
            </div>
            <button style={S.out} onClick={() => signOut(st.auth)}>Sign in with a different account</button>
          </>
        ) : (
          <>
            <div style={S.sub}>Invite-only dashboard. Sign in to continue.</div>
            {st.error && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>{st.error}</div>}
            <button style={S.btn} onClick={doSignIn}>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.7 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/><path fill="#FBBC05" d="M10.5 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.9-6.2z"/><path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.6l-7.7-6c-2.1 1.4-4.8 2.3-7.5 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.5 42.6 14.6 48 24 48z"/></svg>
              Sign in with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
}
